import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Slide,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import Editor from '@monaco-editor/react';
import {
  Close,
  CloudUploadOutlined,
  HistoryOutlined,
  RefreshOutlined,
  SaveOutlined,
} from '@mui/icons-material';
import { fetchData, postData } from '../../helpers/apiUtils';
import { HubSite } from './types';
import { DESIGN_HELP, HelpLegend } from './help';
import JsEbenenPanel from './JsEbenenPanel';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface Props {
  open: boolean;
  sites: HubSite[];
  onClose: () => void;
}

/**
 * Eine CSS-Ebene im Editor.
 *
 * ⚠ `status` ist der Kern dieses Dialogs, nicht Kosmetik. Vor 2026-08-21 gab es ihn nicht: das
 * Feld war entweder gefüllt oder leer, und „noch nicht geladen" sah aus wie „ist leer". Wer in
 * diesem Zustand speicherte, ersetzte die komplette Ebene durch das, was er gerade getippt hatte —
 * so verschwand FTs Portal-Anpassung (3.763 Zeichen). Solange `status !== 'ready'` ist, darf
 * NICHTS gespeichert oder ausgespielt werden; ein Ladefehler bleibt sichtbar stehen und wird
 * niemals in einen leeren Text übersetzt.
 */
type LayerStatus = 'idle' | 'loading' | 'ready' | 'error';

interface Layer {
  status: LayerStatus;
  /** Der bearbeitete Text. Nur bei `ready` aussagekräftig. */
  css: string;
  /** Der zuletzt vom Server bestätigte Text — Grundlage für „geändert?". */
  saved: string;
  /** sha256 des Serverstands, den dieser Editor geladen hat. Reist beim Speichern zurück. */
  sha: string;
  error: string | null;
}

const LEER: Layer = { status: 'idle', css: '', saved: '', sha: '', error: null };

interface CssVersion {
  id: number;
  length: number;
  sha256: string;
  change_note: string | null;
  created_by: string | null;
  created_at: string;
}

/**
 * Centralized profile design: one canonical Base-CSS shared by all portals, plus a
 * small per-portal brand override. "Ausspielen" pushes base+override to a portal as
 * one shared file — every hub profile there restyles at once.
 */
const DesignDialog: React.FC<Props> = ({ open, sites, onClose }) => {
  const [tab, setTab] = useState<'base' | 'override' | 'js'>('base');
  const [base, setBase] = useState<Layer>(LEER);
  const [override, setOverride] = useState<Layer>(LEER);
  const [siteKey, setSiteKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{
    severity: 'success' | 'info' | 'warning' | 'error';
    text: string;
  } | null>(null);
  const [versions, setVersions] = useState<CssVersion[] | null>(null);
  const [versionsFor, setVersionsFor] = useState<string>('');
  const [leerFrage, setLeerFrage] = useState<{ scope: 'base' | 'override'; bisher: number } | null>(
    null,
  );

  const aktiv = tab === 'base' ? base : override;
  const dirty = aktiv.status === 'ready' && aktiv.css !== aktiv.saved;
  /** Ein Knopf, der schreibt oder ausspielt, darf NIE auf einem ungeladenen Stand arbeiten. */
  const bereit = aktiv.status === 'ready' && !busy;
  const deployableSites = sites.filter((s) =>
    ['planned', 'live'].includes(s.receiver_status ?? ''),
  );

  // ---------------------------------------------------------------- Laden

  const ladeBase = useCallback(async () => {
    setBase((p) => ({ ...p, status: 'loading', error: null }));
    try {
      const r = await fetchData<{ css: string; sha256: string }>(
        '/api/broker-hub/settings/base-css',
      );
      // ⚠ Kein `r.success ? r.data : {}`. Ein fehlgeschlagener Abruf ist ein FEHLER, kein leeres
      // CSS — der Unterschied ist genau der Vorfall, den dieser Dialog nicht wiederholen darf.
      if (!r.success || !r.data) throw new Error(r.error || 'Basis-CSS konnte nicht geladen werden');
      setBase({
        status: 'ready',
        css: r.data.css,
        saved: r.data.css,
        sha: r.data.sha256,
        error: null,
      });
    } catch (e) {
      setBase({ ...LEER, status: 'error', error: (e as Error).message });
    }
  }, []);

  const ladeOverride = useCallback(async (key: string, abgebrochen: () => boolean) => {
    if (!key) {
      setOverride(LEER);
      return;
    }
    setOverride((p) => ({ ...p, status: 'loading', error: null }));
    try {
      const r = await fetchData<{ css: string; sha256: string }>(
        `/api/broker-hub/sites/${key}/override-css`,
      );
      if (abgebrochen()) return;
      if (!r.success || !r.data) {
        throw new Error(r.error || `Portal-Anpassung (${key.toUpperCase()}) nicht ladbar`);
      }
      setOverride({
        status: 'ready',
        css: r.data.css,
        saved: r.data.css,
        sha: r.data.sha256,
        error: null,
      });
    } catch (e) {
      if (abgebrochen()) return;
      setOverride({ ...LEER, status: 'error', error: (e as Error).message });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    setVersions(null);
    void ladeBase();
    // Das vorausgewählte Portal setzen — geladen wird es vom Effekt darunter, NICHT hier und
    // erst recht nicht nur im onChange des Selects. Genau diese Lücke war der Vorfall: FT ist
    // id=1 und damit immer vorausgewählt, also feuerte für FT nie ein onChange.
    setSiteKey((vorher) => {
      if (vorher) return vorher;
      const deployable = sites.find((s) => ['planned', 'live'].includes(s.receiver_status ?? ''));
      return deployable?.site_key ?? sites[0]?.site_key ?? '';
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // ⚠ Abbruchwache: beim schnellen Umschalten darf die verspätete Antwort von Portal A nicht
    // den Editor überschreiben, in dem inzwischen Portal B steht.
    let tot = false;
    void ladeOverride(siteKey, () => tot);
    setVersions(null);
    return () => {
      tot = true;
    };
  }, [open, siteKey, ladeOverride]);

  // ---------------------------------------------------------------- Speichern

  const speichern = async (scope: 'base' | 'override', allowEmpty = false) => {
    const layer = scope === 'base' ? base : override;
    const setLayer = scope === 'base' ? setBase : setOverride;
    if (layer.status !== 'ready') return;
    const url =
      scope === 'base'
        ? '/api/broker-hub/settings/base-css'
        : `/api/broker-hub/sites/${siteKey}/override-css`;

    setBusy(true);
    setMessage(null);
    const res = await fetchData<{ css: string; sha256: string }>(url, {
      method: 'PUT',
      body: JSON.stringify({ css: layer.css, expectedSha: layer.sha, allowEmpty }),
    });
    setBusy(false);

    const code = (res as any)?.code as string | undefined;
    if (code === 'css_empty') {
      // Löschen bleibt möglich — aber als bewusste zweite Runde, nicht als Nebenwirkung.
      setLeerFrage({ scope, bisher: (res as any)?.details?.previousLength ?? layer.saved.length });
      return;
    }
    if (code === 'css_conflict') {
      setMessage({
        severity: 'error',
        text:
          `${res.error} ` +
          'Deine Eingabe steht noch im Editor — kopiere sie dir heraus, lade neu und wende sie erneut an.',
      });
      return;
    }
    // ⚠ Auf die FORM prüfen, nicht nur auf `success`. Eine 200-Antwort ohne `css`/`sha256` (älteres
    // Backend, Proxy, das den Rumpf ersetzt) würde sonst einen undefinierten `sha` in den Zustand
    // schreiben — und die optimistische Sperre wäre beim nächsten Speichern wirkungslos, also
    // genau der Schutz weg, um den es hier geht. Lieber ein sichtbarer Fehler.
    if (!res.success || typeof res.data?.css !== 'string' || typeof res.data?.sha256 !== 'string') {
      setMessage({
        severity: 'error',
        text: res.error || 'Speichern fehlgeschlagen — die Antwort des Servers war unvollständig.',
      });
      return;
    }

    setLayer({
      status: 'ready',
      css: res.data.css,
      saved: res.data.css,
      sha: res.data.sha256,
      error: null,
    });
    if (versionsFor === versionsSchluessel(scope)) void ladeVersionen(scope);
    setMessage({
      severity: 'success',
      text:
        scope === 'base'
          ? `Basis-CSS gespeichert (${layer.saved.length} → ${res.data.css.length} Zeichen). ` +
            'Sichtbar wird es erst mit „Seiten neu ausspielen" je Portal.'
          : `Portal-Anpassung für ${siteKey.toUpperCase()} gespeichert (${layer.saved.length} → ${res.data.css.length} Zeichen). ` +
            'Sichtbar wird sie erst mit „Seiten neu ausspielen".',
    });
  };

  // ---------------------------------------------------------------- Fassungen

  const versionsSchluessel = (scope: 'base' | 'override') => (scope === 'base' ? 'base' : siteKey);

  const ladeVersionen = async (scope: 'base' | 'override') => {
    const key = versionsSchluessel(scope);
    if (!key) return;
    setVersionsFor(key);
    const r = await fetchData<CssVersion[]>(`/api/broker-hub/css-versions/${key}`);
    setVersions(r.success && Array.isArray(r.data) ? r.data : []);
  };

  const wiederherstellen = async (id: number, redeploy: boolean) => {
    setBusy(true);
    setMessage(null);
    const res = await postData<{
      scope: string;
      siteKey: string | null;
      length: number;
      sha256: string;
      redeployed: { total: number; ok: number; failed: number; results?: any[] } | null;
    }>(`/api/broker-hub/css-version/${id}/restore`, { redeploy });
    setBusy(false);
    if (!res.success || !res.data) {
      setMessage({ severity: 'error', text: res.error || 'Wiederherstellen fehlgeschlagen' });
      return;
    }
    const d = res.data;
    if (tab === 'base') await ladeBase();
    else await ladeOverride(siteKey, () => false);
    await ladeVersionen(tab === 'base' ? 'base' : 'override');

    if (!d.redeployed) {
      // ⚠ Kein pauschales „wiederhergestellt". Die Datenbank ist zurück, die öffentliche Seite
      // nicht — bei Blocks-Portalen reist die CSS im Seiten-HTML mit.
      setMessage({
        severity: 'info',
        text: `Fassung ${id} ist wieder aktiv (${d.length} Zeichen) — in der Datenbank. Die Live-Seiten zeigen sie erst nach „Seiten neu ausspielen“.`,
      });
      return;
    }
    const kaputt = (d.redeployed.results ?? []).filter((r: any) => !r.ok);
    setMessage({
      severity: d.redeployed.failed ? 'warning' : 'success',
      text: d.redeployed.failed
        ? `Fassung ${id} aktiv, ${d.redeployed.ok}/${d.redeployed.total} Seiten neu ausgespielt. Fehlgeschlagen: ${kaputt
            .map((r: any) => `${r.slug} (${r.error ?? 'unbekannt'})`)
            .join(', ')}`
        : `Fassung ${id} aktiv und alle ${d.redeployed.ok} Seiten neu ausgespielt — live.`,
    });
  };

  // ---------------------------------------------------------------- Ausspielen

  const deployBase = async (key: string) => {
    setBusy(true);
    setMessage(null);
    const res = await postData<{ ok: boolean; error?: string }>(
      `/api/broker-hub/sites/${key}/deploy-base-css`,
      {},
    );
    setBusy(false);
    if (res.success && (res.data as any)?.ok) {
      // Kein pauschales „ausgespielt": die CSS-DATEI ist geschrieben, aber ob ein Portal sie laedt,
      // haengt am Portal. FT tut es nicht (gemessen 2026-08-08: null Treffer fuer die
      // Basis-Selektoren ueber alle Stylesheets der Brokerprofil-Seite) — dort reist die CSS
      // stattdessen im eingebetteten <style> der gerenderten Seite mit und wird erst mit dem
      // naechsten PROFIL-Deploy sichtbar. Eine gruene Erfolgsmeldung, die das verschweigt, laesst
      // einen Redakteur auf eine Wirkung warten, die nie eintritt.
      setMessage({
        severity: 'info',
        text:
          `CSS-Datei fuer ${key.toUpperCase()} geschrieben. Blocks-Profile uebernehmen sie erst ` +
          'mit ihrem naechsten Deploy — die Seite laedt die Datei nicht, die CSS reist im Seiten-HTML mit.',
      });
    } else {
      setMessage({
        severity: 'error',
        text: `Ausspielen (${key.toUpperCase()}) fehlgeschlagen: ${(res.data as any)?.error || res.error}`,
      });
    }
  };

  const deployAll = async () => {
    const targets = sites.filter((s) => ['planned', 'live'].includes(s.receiver_status ?? ''));
    setBusy(true);
    setMessage(null);
    const results: string[] = [];
    for (const s of targets) {
      const res = await postData<{ ok: boolean; error?: string }>(
        `/api/broker-hub/sites/${s.site_key}/deploy-base-css`,
        {},
      );
      results.push(
        `${s.site_key.toUpperCase()}: ${res.success && (res.data as any)?.ok ? 'ok' : 'FEHLER'}`,
      );
    }
    setBusy(false);
    // Wie bei deployBase: geschrieben ist nicht gleich wirksam — siehe Kommentar dort.
    setMessage({
      severity: results.some((r) => r.includes('FEHLER')) ? 'warning' : 'info',
      text: `CSS-Dateien geschrieben — ${results.join(', ')}. Blocks-Profile uebernehmen sie mit ihrem naechsten Deploy.`,
    });
  };

  // Der Weg, der eine CSS-Aenderung auf einem Blocks-Portal WIRKLICH sichtbar macht: dort reist die
  // CSS im Seiten-HTML mit (siehe Kommentar in deployBase), also muss jede Seite neu ausgespielt
  // werden. Der Server nimmt nur Live-Seiten — ein Deploy auf einem Entwurf waere ein Takedown.
  const redeployAll = async (key: string) => {
    setBusy(true);
    setMessage(null);
    const res = await postData<{
      total: number;
      ok: number;
      failed: number;
      results?: Array<{ slug: string; ok: boolean; error?: string }>;
    }>(`/api/broker-hub/sites/${key}/redeploy-all`, {});
    setBusy(false);
    const d = res.data as
      | { total: number; ok: number; failed: number; results?: any[] }
      | undefined;
    if (!res.success || !d) {
      setMessage({ severity: 'error', text: res.error || 'Neu ausspielen fehlgeschlagen' });
      return;
    }
    // Teilausfall darf nicht als Erfolg durchgehen — die fehlgeschlagenen Seiten beim Namen nennen.
    const kaputt = (d.results ?? []).filter((r) => !r.ok);
    setMessage({
      severity: d.failed ? 'warning' : 'success',
      text: d.failed
        ? `${d.ok}/${d.total} Seiten neu ausgespielt. Fehlgeschlagen: ${kaputt
            .map((r) => `${r.slug} (${r.error ?? 'unbekannt'})`)
            .join(', ')}`
        : `${d.ok} Seiten von ${key.toUpperCase()} neu ausgespielt — die CSS-Aenderung ist live.`,
    });
  };

  // ---------------------------------------------------------------- Darstellung

  const ladeHinweis = (layer: Layer, neuLaden: () => void) => {
    if (layer.status === 'loading') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1} p={4}>
          <CircularProgress size={20} />
          <Typography variant="body2">Bestand wird geladen …</Typography>
        </Box>
      );
    }
    if (layer.status === 'error') {
      return (
        <Box p={3}>
          <Alert
            severity="error"
            action={
              <Button size="small" startIcon={<RefreshOutlined />} onClick={neuLaden}>
                Erneut laden
              </Button>
            }
          >
            {layer.error} — <strong>nicht bearbeitbar</strong>, solange der Bestand unbekannt ist.
            Ein leeres Feld hier wäre gefährlicher als gar keins.
          </Alert>
        </Box>
      );
    }
    return null;
  };

  /**
   * Dem AKTIVEN Stand eine Fassung geben, ohne ihn zu ändern.
   *
   * ⚠ Nur sichtbar, wenn es zum aktuellen Stand KEINE Fassung gibt. Der Fall entsteht, wenn
   * jemand am Dienst vorbei direkt in die Tabelle geschrieben hat (Notfallwerkzeug
   * `test/seed_ft_layout_css.ts`) — der Wächter meldet das, und es gab bis 2026-09-13 keinen Weg
   * zurück: byte-gleich speichern legt keine Fassung an, der Dienst steigt vorher aus.
   *
   * ⚠ Der Knopf ändert NICHTS am Inhalt und löst kein Ausspielen aus. Und er macht den
   * Schreibvorgang am Dienst vorbei nicht ungeschehen: die Fassung trägt das als Notiz.
   */
  const fassungAusBestand = async (scope: 'base' | 'override') => {
    const key = versionsSchluessel(scope);
    if (!key) return;
    setBusy(true);
    setMessage(null);
    const res = await postData<{ angelegt: boolean; versionId: number | null; length: number }>(
      `/api/broker-hub/css-versions/${key}/snapshot`,
      {},
    );
    setBusy(false);
    if (!res.success || !res.data) {
      setMessage({ severity: 'error', text: res.error || 'Fassung konnte nicht angelegt werden' });
      return;
    }
    await ladeVersionen(scope);
    setMessage({
      severity: 'success',
      text: res.data.angelegt
        ? `Fassung ${res.data.versionId} aus dem aktuellen Stand angelegt (${res.data.length} Zeichen). Am Inhalt hat sich nichts geändert — die Live-Seiten bleiben, wie sie sind.`
        : `Zu diesem Stand gab es bereits Fassung ${res.data.versionId} — nichts angelegt.`,
    });
  };

  /**
   * Kein Eintrag der Liste passt zum aktiven Stand — dann fehlt der Wiederherstellungspunkt.
   *
   * ⚠ Die Sichtbarkeit des Knopfs haengt zusaetzlich an `aktiv.saved.trim()`, NICHT an `aktiv.sha`.
   * Der sha ist auch fuer leeres CSS gefuellt (`cssSha256('')` ist der Hash des Leerstrings, nicht
   * der Leerstring) — mit ihm als Bedingung erschien der Knopf auf einem Portal ganz ohne
   * Portal-Anpassung, wo der Dienst ihn zwangslaeufig mit 400 abweist. Betraf bg: leerer Override,
   * null Fassungen. `saved` ist im Lade- wie im Fehlerzustand '' und deckt damit auch das Flackern
   * waehrend des Ladens ab.
   */
  const ohneFassung = (versions ?? []).every((v) => v.sha256 !== aktiv.sha);

  const bestandsknopf = (scope: 'base' | 'override') => (
    <Box p={1.5} borderTop="1px solid #e0e0e0">
      <Tooltip title="Legt eine Fassung mit genau dem an, was jetzt aktiv ist. Ändert den Inhalt nicht und spielt nichts aus — nur der Wiederherstellungspunkt fehlt bisher.">
        <span>
          <Button size="small" variant="outlined" disabled={busy} onClick={() => fassungAusBestand(scope)}>
            Fassung aus dem aktuellen Stand
          </Button>
        </span>
      </Tooltip>
      <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
        Zum aktiven Stand gibt es keine Fassung — er wurde am Dienst vorbei geschrieben.
      </Typography>
    </Box>
  );

  const fassungsliste = (scope: 'base' | 'override') => (
    <Box mt={1} border="1px solid #e0e0e0" maxHeight={220} overflow="auto">
      {versions === null ? (
        <Box p={1.5}>
          <Button size="small" startIcon={<HistoryOutlined />} onClick={() => ladeVersionen(scope)}>
            Frühere Fassungen anzeigen
          </Button>
        </Box>
      ) : versions.length === 0 ? (
        <>
          <Box p={1.5}>
            <Typography variant="body2" color="text.secondary">
              Noch keine Fassungen aufgezeichnet.
            </Typography>
          </Box>
          {aktiv.saved.trim() !== '' && ohneFassung && bestandsknopf(scope)}
        </>
      ) : (
        <List dense disablePadding>
          {versions.map((v) => {
            const aktuell = v.sha256 === aktiv.sha;
            return (
              <ListItemButton key={v.id} disableRipple sx={{ alignItems: 'flex-start' }}>
                <ListItemText
                  primary={
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2">
                        {new Date(v.created_at).toLocaleString('de-DE')} · {v.length} Zeichen
                      </Typography>
                      {aktuell && <Chip size="small" color="success" label="aktiv" />}
                    </Box>
                  }
                  secondary={`${v.created_by ?? 'unbekannt'}${v.change_note ? ` · ${v.change_note}` : ''}`}
                />
                {!aktuell && (
                  <Box display="flex" gap={0.5} flexShrink={0}>
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={() => wiederherstellen(v.id, false)}
                    >
                      Zurückholen
                    </Button>
                    {scope === 'override' && (
                      <Tooltip title="Zurückholen und alle Live-Seiten dieses Portals sofort neu ausspielen — nur so ändert sich die öffentliche Seite.">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            disabled={busy}
                            onClick={() => wiederherstellen(v.id, true)}
                          >
                            + neu ausspielen
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}
      {/* Auch bei gefuellter Liste: passt KEINE Fassung zum aktiven Stand, fehlt der
          Wiederherstellungspunkt genauso — nur faellt es dort weniger auf. */}
      {versions !== null && versions.length > 0 && aktiv.saved.trim() !== '' && ohneFassung && bestandsknopf(scope)}
    </Box>
  );

  return (
    <Dialog fullScreen open={open} onClose={onClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: 'relative' }} color="default" elevation={1}>
        <Toolbar variant="dense">
          <Typography variant="h6">Design — Broker-Profile</Typography>
          <Box flexGrow={1} />
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box p={2} display="flex" flexDirection="column" height="100%" overflow="hidden">
        <HelpLegend items={DESIGN_HELP} storageKey="design" />
        {message && (
          <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 1 }}>
            {message.text}
          </Alert>
        )}
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab value="base" label="Basis-CSS (alle Portale)" />
          <Tab value="override" label="Portal-Anpassung" />
          <Tab value="js" label="JavaScript" />
        </Tabs>

        {tab === 'js' && <JsEbenenPanel open={open} sites={sites} />}

        {tab === 'base' && (
          <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
            <Box display="flex" gap={1} mb={1} alignItems="center">
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SaveOutlined />}
                disabled={!dirty || !bereit}
                onClick={() => speichern('base')}
              >
                Speichern
              </Button>
              <Tooltip
                title={
                  dirty ? 'Erst speichern' : 'Basis-CSS auf alle Portale mit Receiver ausspielen'
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadOutlined />}
                    disabled={dirty || !bereit}
                    onClick={deployAll}
                  >
                    Auf alle Portale deployen
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                startIcon={<HistoryOutlined />}
                onClick={() => ladeVersionen('base')}
              >
                Fassungen
              </Button>
              <Box flexGrow={1} />
              {deployableSites.map((s) => (
                <Button
                  key={s.site_key}
                  size="small"
                  disabled={dirty || !bereit}
                  onClick={() => deployBase(s.site_key)}
                >
                  → {s.site_key.toUpperCase()}
                </Button>
              ))}
            </Box>
            <Box flex={1} border="1px solid #e0e0e0">
              {ladeHinweis(base, ladeBase) ?? (
                <Editor
                  height="100%"
                  defaultLanguage="css"
                  path="broker-hub-base.css"
                  value={base.css}
                  onChange={(v) => setBase((p) => ({ ...p, css: v ?? '' }))}
                  options={{
                    minimap: { enabled: false },
                    wordWrap: 'on',
                    fontSize: 13,
                    scrollBeyondLastLine: false,
                  }}
                />
              )}
            </Box>
            {versionsFor === 'base' && fassungsliste('base')}
          </Box>
        )}

        {tab === 'override' && (
          <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
            <Box display="flex" gap={1} mb={1} alignItems="center" flexWrap="wrap">
              <TextField
                select
                size="small"
                label="Portal"
                value={siteKey}
                onChange={(e) => setSiteKey(e.target.value)}
                sx={{ width: 220 }}
              >
                {sites.map((s) => (
                  <MenuItem key={s.site_key} value={s.site_key}>
                    {s.name} ({s.site_key.toUpperCase()})
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SaveOutlined />}
                disabled={!dirty || !bereit || !siteKey}
                onClick={() => speichern('override')}
              >
                Speichern
              </Button>
              <Tooltip
                title={dirty ? 'Erst speichern' : 'Basis + diese Anpassung auf das Portal ausspielen'}
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadOutlined />}
                    disabled={dirty || !bereit || !siteKey}
                    onClick={() => deployBase(siteKey)}
                  >
                    Ausspielen
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  dirty
                    ? 'Erst speichern'
                    : 'Alle Live-Seiten dieses Portals neu ausspielen — nötig, weil Blocks-Seiten ihre CSS im Seiten-HTML tragen. Entwürfe bleiben unberührt.'
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<CloudUploadOutlined />}
                    disabled={dirty || !bereit || !siteKey}
                    onClick={() => redeployAll(siteKey)}
                  >
                    Seiten neu ausspielen
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                startIcon={<HistoryOutlined />}
                disabled={!siteKey}
                onClick={() => ladeVersionen('override')}
              >
                Fassungen
              </Button>
              {aktiv.status === 'ready' && (
                <Typography variant="caption" color="text.secondary">
                  Bestand: {aktiv.saved.length} Zeichen
                  {dirty ? ` → ${aktiv.css.length} nach dem Speichern` : ''}
                </Typography>
              )}
            </Box>
            <Box flex={1} border="1px solid #e0e0e0">
              {ladeHinweis(override, () => void ladeOverride(siteKey, () => false)) ?? (
                <Editor
                  height="100%"
                  defaultLanguage="css"
                  path={`override-${siteKey}.css`}
                  value={override.css}
                  onChange={(v) => setOverride((p) => ({ ...p, css: v ?? '' }))}
                  options={{
                    minimap: { enabled: false },
                    wordWrap: 'on',
                    fontSize: 13,
                    scrollBeyondLastLine: false,
                  }}
                />
              )}
            </Box>
            {versionsFor === siteKey && siteKey !== '' && fassungsliste('override')}
          </Box>
        )}
      </Box>

      {/* Löschen bleibt möglich — aber nur als bewusste zweite Runde mit `allowEmpty`. */}
      <Dialog open={leerFrage !== null} onClose={() => setLeerFrage(null)}>
        <DialogTitle>CSS-Ebene wirklich leeren?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Der bisherige Inhalt hat <strong>{leerFrage?.bisher} Zeichen</strong> und wird durch
            einen leeren Text ersetzt.
          </DialogContentText>
          <Divider sx={{ my: 1.5 }} />
          <DialogContentText variant="body2">
            Die bisherige Fassung bleibt unter „Fassungen“ abrufbar. Wirksam wird die Leerung erst
            nach „Seiten neu ausspielen“.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeerFrage(null)}>Abbrechen</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const f = leerFrage!;
              setLeerFrage(null);
              void speichern(f.scope, true);
            }}
          >
            Leeren
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default DesignDialog;
